import { usableIrr, usableTokens } from "./utils";
import type { Wallet, WalletBalanceItem } from "./types";

/**
 * `/account/wallet/{address}/balance` is keyed by ADDRESS, not by a `Wallet`
 * row's network — an address shared across multiple EVM-compatible networks
 * (the common case: one address, several `Wallet` rows, one per network) gets
 * back every network's assets on EACH of those rows' balance queries,
 * identically. Scoping `allAssets` to this row's own network here is what
 * makes the returned `assets` mean "this wallet's holdings" for every
 * consumer (`useUserBalance`'s own `totalIrr`, `aggregateAssets`, the bid
 * flows' `wallet.assets.find`) — without it, a shared-address user's balance
 * is read once per duplicate network row and summed that many times over
 * (confirmed: a real ~50 USDT balance rendered as ~96 in the navbar wallet
 * popover, while the wallet page — which only ever reads one selected wallet
 * at a time — showed the correct figure).
 *
 * Kept in its own JSX/hook-free module, same reason `review-rows.ts` and
 * `flow.ts` are — so this exact scoping/summing behaviour is unit-testable
 * without a React Query harness.
 */
export function enrichWalletAssets(wallet: Wallet, allAssets: WalletBalanceItem[]) {
  const assets = allAssets.filter((a) => a.network?.id === wallet.network.id);

  const native = findNativeAsset(assets) ?? assets[0];

  // What can be used now: a bid or an offer's locked part is not counted (see `usableTokens`).
  const irrValue = assets.reduce((acc, a) => acc + usableIrr(a), 0);

  return {
    assets,
    nativeBalance: native ? usableTokens(native) : 0,
    nativeSymbol: native?.symbol?.toUpperCase() ?? wallet.network.name.toUpperCase(),
    irrValue,
  };
}


/**
 * The chain’s own coin in a balance list — ETH on Ethereum, POL on Polygon.
 *
 * THE RULE IS THE ZERO ADDRESS, and that is measured, not assumed: the live V2
 * balance endpoint returns the native row as
 * `contract_address: "0x0000000000000000000000000000000000000000"`, with the
 * ticker in `symbol`. `"native_balance"` is kept as a second test because the
 * deposit modal was written against it, but it has never been observed on the
 * wire and matches nothing today.
 *
 * IT EXISTS BECAUSE THERE WERE THREE RULES. This module tested the zero
 * address, the wallet page repeated that inline, and the deposit modal looked
 * for `symbol === "POL" || symbol === "MATIC"` — so the deposit balance was
 * right on Polygon and silently zero on every other chain.
 *
 * NO POSITIONAL FALLBACK HERE. A caller that would rather show something than
 * nothing adds its own `?? assets[0]`; a caller on a money screen does not,
 * because another token's balance under the native token's name is worse than
 * a dash.
 */
export function findNativeAsset<T extends { contract_address?: string | null }>(
  assets: readonly T[],
): T | undefined {
  return (
    assets.find((asset) => asset.contract_address?.startsWith("0x0000")) ??
    assets.find((asset) => asset.contract_address === "native_balance")
  );
}
